/**
 * Admin FAQ Preview Page
 * 
 * Shows exactly how end users will see the FAQ.
 * Purpose: Let admins verify content before publishing.
 * 
 * Features:
 * - Read-only view matching user experience
 * - Responsive video/document embed
 * - Quick edit CTA
 * - Back navigation
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LoomIcon from '@mui/icons-material/VideoLibrary';
import DocsIcon from '@mui/icons-material/Description';
import SheetsIcon from '@mui/icons-material/TableChart';
import SlidesIcon from '@mui/icons-material/Slideshow';
import DriveIcon from '@mui/icons-material/Folder';

import MainCard from '../../../components/MainCard';
import CustomButton from '../../../components/@extended/CustomButton';
import { useGetFaqByIdQuery } from '../../../store/slices/apiSlice';

// Get embed URL for media
const getEmbedUrl = (provider, resourceId) => {
  if (!resourceId) return null;
  switch (provider) {
    case 'YOUTUBE':
      return `https://www.youtube.com/embed/${resourceId}`;
    case 'LOOM':
      return `https://www.loom.com/embed/${resourceId}`;
    case 'GOOGLE_DRIVE':
      return `https://drive.google.com/file/d/${resourceId}/preview`;
    case 'GOOGLE_DOCS':
      return `https://docs.google.com/document/d/${resourceId}/preview`;
    case 'GOOGLE_SHEETS':
      return `https://docs.google.com/spreadsheets/d/${resourceId}/preview`;
    case 'GOOGLE_SLIDES':
      return `https://docs.google.com/presentation/d/${resourceId}/embed`;
    default:
      return null;
  }
};

// Provider icon component
const ProviderIcon = ({ provider, size = 20 }) => {
  const iconProps = { sx: { fontSize: size } };
  switch (provider) {
    case 'YOUTUBE':
      return <YouTubeIcon {...iconProps} sx={{ ...iconProps.sx, color: '#FF0000' }} />;
    case 'LOOM':
      return <LoomIcon {...iconProps} sx={{ ...iconProps.sx, color: '#625DF5' }} />;
    case 'GOOGLE_DOCS':
      return <DocsIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4285F4' }} />;
    case 'GOOGLE_SHEETS':
      return <SheetsIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0F9D58' }} />;
    case 'GOOGLE_SLIDES':
      return <SlidesIcon {...iconProps} sx={{ ...iconProps.sx, color: '#F4B400' }} />;
    case 'GOOGLE_DRIVE':
      return <DriveIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4285F4' }} />;
    default:
      return null;
  }
};

// Get provider display name
const getProviderName = (provider) => {
  const names = {
    YOUTUBE: 'Video Tutorial',
    LOOM: 'Video Tutorial',
    GOOGLE_DOCS: 'Document',
    GOOGLE_SHEETS: 'Spreadsheet',
    GOOGLE_SLIDES: 'Presentation',
    GOOGLE_DRIVE: 'File',
  };
  return names[provider] || 'Media';
};

// Check if provider is video type
const isVideoProvider = (provider) => ['YOUTUBE', 'LOOM'].includes(provider);

// Media embed component
const MediaEmbed = ({ provider, resourceId }) => {
  const embedUrl = getEmbedUrl(provider, resourceId);
  
  if (!embedUrl) return null;

  const isVideo = isVideoProvider(provider);

  return (
    <Box sx={{ mt: 4, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ProviderIcon provider={provider} />
        <Typography variant="subtitle2" color="text.secondary">
          {getProviderName(provider)}
        </Typography>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          paddingBottom: isVideo ? '56.25%' : '75%', // 16:9 for video, 4:3 for docs
          height: 0,
          overflow: 'hidden',
          borderRadius: 2,
          backgroundColor: 'grey.100',
          boxShadow: 1,
        }}
      >
        <iframe
          src={embedUrl}
          title="FAQ Media"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Box>
    </Box>
  );
};

// Status badge
const StatusBadge = ({ isPublished }) => (
  <Chip
    label={isPublished ? 'Published' : 'Draft'}
    size="small"
    sx={{
      backgroundColor: isPublished ? '#E8F5E9' : '#FFF3E0',
      color: isPublished ? '#2E7D32' : '#E65100',
      fontWeight: 500,
    }}
  />
);

const FaqPreview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { data: faq, isLoading, error } = useGetFaqByIdQuery(id);

  // Loading state
  if (isLoading) {
    return (
      <MainCard>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="text" width="30%" sx={{ mt: 2, mb: 4 }} />
        <Skeleton variant="rectangular" height={200} />
      </MainCard>
    );
  }

  // Error state
  if (error || !faq) {
    return (
      <MainCard title="Preview Error">
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load FAQ. The FAQ may have been deleted or you don't have permission to view it.
        </Alert>
        <CustomButton variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/admin/faqs')}>
          Back to FAQ List
        </CustomButton>
      </MainCard>
    );
  }

  return (
    <MainCard
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CustomButton
            variant="text"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin/faqs')}
            sx={{ mr: 1 }}
          >
            Back
          </CustomButton>
          <Typography variant="h5">FAQ Preview</Typography>
        </Box>
      }
      secondary={
        <CustomButton
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/faqs/${id}/edit`)}
          size="small"
        >
          Edit FAQ
        </CustomButton>
      }
    >
      {/* Admin Info Banner */}
      <Alert 
        severity="info" 
        sx={{ mb: 3 }}
        icon={false}
      >
        <Typography variant="body2">
          <strong>Admin Preview</strong> — This is how end users will see this FAQ.
        </Typography>
      </Alert>

      {/* Meta Information */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <StatusBadge isPublished={faq.isPublished} />
        {faq.category && (
          <Chip
            icon={<CategoryIcon sx={{ fontSize: '16px !important' }} />}
            label={faq.category.name}
            size="small"
            variant="outlined"
            sx={{
              opacity: faq.category.isActive ? 1 : 0.6,
            }}
          />
        )}
        {faq.category && !faq.category.isActive && (
          <Chip
            label="Category Inactive"
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
        {faq.videoProvider && faq.videoProvider !== 'NONE' && (
          <Chip
            icon={<ProviderIcon provider={faq.videoProvider} size={14} />}
            label={getProviderName(faq.videoProvider)}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* FAQ Content Preview */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          backgroundColor: 'background.default',
        }}
      >
        {/* Question */}
        <Typography 
          variant="h4" 
          component="h1" 
          fontWeight={600}
          sx={{ mb: 3 }}
        >
          {faq.question}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Answer - with tight, consistent spacing */}
        <Box sx={{ maxWidth: 720 }}>
          <Typography 
            variant="body1" 
            component="div"
            sx={{ 
              lineHeight: 1.6,
              color: 'text.secondary',
              // Paragraph styling - minimal spacing
              '& p': { 
                m: 0,
                mb: 0.5,
                '&:last-child': { mb: 0 },
                '&:empty': { display: 'none' },
              },
              '& br': { lineHeight: 0.5 },
              // Lists - compact styling
              '& ul, & ol': { 
                pl: 2.5, 
                my: 0.5,
                '& li': { 
                  mb: 0.25,
                  lineHeight: 1.5,
                  '& p': { m: 0 },
                },
              },
              '& strong, & b': {
                color: 'text.primary',
                fontWeight: 600,
              },
              '& code': {
                backgroundColor: 'grey.100',
                padding: '1px 4px',
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& blockquote': {
                borderLeft: 2,
                borderColor: 'grey.300',
                pl: 1.5,
                ml: 0,
                my: 0.5,
                '& p': { m: 0 },
              },
              '& h1, & h2, & h3': {
                color: 'text.primary',
                mt: 1,
                mb: 0.5,
                lineHeight: 1.3,
                '&:first-of-type': { mt: 0 },
              },
              '& h1': { fontSize: '1.25rem', fontWeight: 600 },
              '& h2': { fontSize: '1.1rem', fontWeight: 600 },
              '& h3': { fontSize: '1rem', fontWeight: 600 },
            }}
            dangerouslySetInnerHTML={{ __html: faq.answer }}
          />
        </Box>

        {/* Media Embed */}
        {faq.videoProvider !== 'NONE' && faq.videoId && (
          <MediaEmbed provider={faq.videoProvider} resourceId={faq.videoId} />
        )}
      </Paper>

      {/* Footer Meta */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={4}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">
              {new Date(faq.createdAt).toLocaleDateString()} by {faq.createdBy || 'Unknown'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Last Updated
            </Typography>
            <Typography variant="body2">
              {new Date(faq.updatedAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Display Order
            </Typography>
            <Typography variant="body2">
              {faq.displayOrder}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </MainCard>
  );
};

export default FaqPreview;
